import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, authedFileUrl } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { CheckCircle2, Upload, ArrowRight, X } from "lucide-react";

/** 3-step onboarding modal that pops the first time a buyer logs in with an
 * empty company profile. Steps: company info -> logo -> first driver. Each
 * step saves immediately so a buyer can quit halfway and resume later. */
export default function SetupWizard({ open, settings, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company_name: "", address: "", phone: "", email: "", ein: "",
  });
  const [driver, setDriver] = useState({
    name: "", display_id: "", pay_type: "per_percentage", pay_rate: 50,
    base_rate_per_mile: 3.05, default_back_office_percent: 35,
  });
  const [saving, setSaving] = useState(false);
  const [logoKey, setLogoKey] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    if (settings) {
      setForm({
        company_name: settings.company_name || "",
        address: settings.address || "",
        phone: settings.phone || "",
        email: settings.email || "",
        ein: settings.ein || "",
      });
    }
  }, [settings]);

  const logoUrl = settings?.logo_path
    ? `${authedFileUrl("/settings/logo")}&v=${logoKey}` : null;

  const saveCompany = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return false; }
    setSaving(true);
    try {
      await api.put("/settings", { ...settings, ...form });
      toast.success("Company info saved");
      return true;
    } catch (e) {
      toast.error("Save failed"); return false;
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoKey((k) => k + 1);
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const saveDriver = async () => {
    if (!driver.name.trim()) { toast.error("Driver name is required"); return false; }
    setSaving(true);
    try {
      const payload = {
        name: driver.name.trim(),
        pay_type: driver.pay_type,
        pay_rate: parseFloat(driver.pay_rate) || 0,
        role: "single",
        active: true,
        base_rate_per_mile: parseFloat(driver.base_rate_per_mile) || 0,
        default_back_office_percent: parseFloat(driver.default_back_office_percent) || 35,
      };
      if (driver.display_id?.trim()) payload.display_id = driver.display_id.trim();
      await api.post("/drivers", payload);
      toast.success("First driver added");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed"); return false;
    } finally { setSaving(false); }
  };

  const next = async () => {
    if (step === 1) {
      const ok = await saveCompany(); if (!ok) return;
      setStep(2); return;
    }
    if (step === 2) { setStep(3); return; }
    if (step === 3) {
      if (driver.name.trim()) { const ok = await saveDriver(); if (!ok) return; }
      onComplete?.(); onClose();
    }
  };

  const skip = () => {
    toast.message("Setup saved — finish anytime from Settings");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && skip()}>
      <DialogContent className="max-w-2xl" data-testid="setup-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Welcome — let's set up your fleet
            <button type="button" onClick={skip} className="ml-auto text-slate-400 hover:text-slate-700" aria-label="Skip">
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mt-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-2">
              <div className={
                "w-7 h-7 rounded-full grid place-items-center text-xs font-bold " +
                (step >= n ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500")
              }>{step > n ? <CheckCircle2 size={14} /> : n}</div>
              <div className="text-xs text-slate-600 whitespace-nowrap">
                {n === 1 && "Company"}
                {n === 2 && "Logo"}
                {n === 3 && "First driver"}
              </div>
              {n < 3 && <div className={"flex-1 h-0.5 " + (step > n ? "bg-blue-600" : "bg-slate-200")} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4 mt-4" data-testid="wiz-step-1">
            <div className="text-sm text-slate-600">Fill in the basics. This info prints on every pay statement and shows at the top of your app.</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Company name *</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="wiz-company-name" placeholder="e.g. Acme Transport LLC" /></div>
              <div className="col-span-2"><Label>Street address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="wiz-address" placeholder="123 Main St, City, ST 00000" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="wiz-phone" placeholder="(555) 123-4567" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="wiz-email" placeholder="dispatch@example.com" /></div>
              <div className="col-span-2"><Label>EIN / company tax ID (optional)</Label><Input value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} data-testid="wiz-ein" placeholder="00-0000000" /></div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 mt-4" data-testid="wiz-step-2">
            <div className="text-sm text-slate-600">Upload your company logo. It'll appear at the top of every pay statement and in the app header. You can change it anytime from Settings.</div>
            <div className="flex items-center gap-4">
              <div className="w-32 h-32 rounded border-2 border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden">
                {logoUrl ? <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" /> : <div className="text-slate-400 text-xs">No logo yet</div>}
              </div>
              <div>
                <Button onClick={() => fileRef.current?.click()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="wiz-upload-btn">
                  <Upload size={14} className="mr-1" /> {logoUrl ? "Replace logo" : "Upload logo"}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="wiz-logo-input" />
                <div className="text-[11px] text-slate-500 mt-2">PNG, JPG, or SVG · under 5 MB · square works best.</div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 mt-4" data-testid="wiz-step-3">
            <div className="text-sm text-slate-600">Add your first driver so you can start logging trips. Skip and add drivers later from the Drivers page if you prefer.</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Driver name</Label><Input value={driver.name} onChange={(e) => setDriver({ ...driver, name: e.target.value })} data-testid="wiz-driver-name" placeholder="John Smith" /></div>
              <div><Label>Driver ID (optional)</Label><Input value={driver.display_id} onChange={(e) => setDriver({ ...driver, display_id: e.target.value })} data-testid="wiz-driver-id" placeholder="auto-generated" /></div>
              <div>
                <Label>Pay type</Label>
                <select value={driver.pay_type} onChange={(e) => setDriver({ ...driver, pay_type: e.target.value })} className="w-full h-9 border border-slate-300 rounded px-2 text-sm" data-testid="wiz-driver-paytype">
                  <option value="per_percentage">Per Percentage</option>
                  <option value="per_mile">Per Mile</option>
                </select>
              </div>
              {driver.pay_type === "per_mile" ? (
                <div><Label>Rate ($/mile)</Label><Input type="number" step="0.01" value={driver.pay_rate} onChange={(e) => setDriver({ ...driver, pay_rate: e.target.value })} data-testid="wiz-driver-rate" /></div>
              ) : (
                <>
                  <div><Label>Driver's share %</Label><Input type="number" value={driver.pay_rate} onChange={(e) => setDriver({ ...driver, pay_rate: e.target.value })} data-testid="wiz-driver-rate" /></div>
                  <div><Label>Back office %</Label><Input type="number" value={driver.default_back_office_percent} onChange={(e) => setDriver({ ...driver, default_back_office_percent: e.target.value })} data-testid="wiz-driver-bo" /></div>
                  <div><Label>Base rate ($/mile)</Label><Input type="number" step="0.01" value={driver.base_rate_per_mile} onChange={(e) => setDriver({ ...driver, base_rate_per_mile: e.target.value })} data-testid="wiz-driver-baserate" /></div>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={skip} data-testid="wiz-skip-btn">Skip for now</Button>
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wiz-back-btn">Back</Button>}
          <Button onClick={next} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="wiz-next-btn">
            {step === 3 ? (saving ? "Saving…" : "Finish") : <>Next <ArrowRight size={14} className="ml-1" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
